import { Button, type ButtonProps } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { Column } from '@tanstack/react-table'
import { type TranslationKey, useTranslate } from '@tolgee/react'
import { ChevronDown, ChevronUp, ColumnsIcon } from 'lucide-react'
import React from 'react'
import { Popover, PopoverContent, PopoverTrigger } from './popover'
import { FunnelIcon } from '../icons/funnel-icon'
import { Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from './tooltip'

type TableColumnHeaderSort = {
  tooltip: {
    default: TranslationKey
    desc: TranslationKey
  }
}

export function DataTableColumnHeader<TData, TValue>({
  column,
  children,
  className,
  titleKey,
  columnSort,
  FilterComp,
  ...props
}: React.PropsWithChildren<
  {
    column: Column<TData, TValue>
    titleKey: TranslationKey
    columnSort?: TableColumnHeaderSort
    FilterComp?: React.ComponentType<{
      column: Column<TData, unknown>
      openFilter: boolean
      setOpenFilter: SetState<boolean>
    }>
  } & ButtonProps
>) {
  const { t } = useTranslate()
  const [openFilter, setOpenFilter] = React.useState(false)
  const sortedValue = column.getIsSorted()
  const tooltip = React.useMemo(() => {
    if (sortedValue === 'asc') {
      return t('shared.table.column.sort.asc')
    }
    if (columnSort?.tooltip) {
      return t(columnSort?.tooltip[sortedValue || 'default'])
    }
    return t(`shared.table.column.sort.${sortedValue || 'default'}`)
  }, [columnSort?.tooltip, sortedValue])

  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>
        <Button
          unstyled
          className={cn('group/header-column-cell flex w-full items-center gap-2', className)}
          onClick={column.getToggleSortingHandler()}
          {...props}
        >
          {children}

          {column.getCanSort() && (
            <div className='flex size-5 flex-col items-center gap-0 text-neutral-200 group-hover/header-column-cell:text-neutral-500'>
              <ChevronUp
                className={`h-auto w-[10px] shrink-0 ${sortedValue === 'desc' && 'text-mango-300'}`}
              />
              <ChevronDown
                className={`h-auto w-[10px] shrink-0 ${sortedValue === 'asc' && 'text-mango-300'}`}
              />
            </div>
          )}

          {column.getCanFilter() && (
            <Popover open={openFilter} onOpenChange={setOpenFilter}>
              <PopoverTrigger asChild>
                <div
                  role='button'
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      setOpenFilter((prev) => !prev)
                    }
                  }}
                  onClick={(e) => {
                    e.stopPropagation()
                    setOpenFilter((prev) => !prev)
                  }}
                >
                  <FunnelIcon className='text-neutral-200 hover:text-neutral-500' />
                </div>
              </PopoverTrigger>
              <PopoverContent
                align='start'
                onClick={(e) => e.stopPropagation()}
                className='w-full rounded-lg border-white bg-neutral-25 p-0'
              >
                {FilterComp && (
                  <FilterComp
                    column={column}
                    openFilter={openFilter}
                    setOpenFilter={setOpenFilter}
                  />
                )}
              </PopoverContent>
            </Popover>
          )}
        </Button>
      </TooltipTrigger>
      {column.getCanSort() && (
        <TooltipPortal>
          <TooltipContent side='bottom' align='center' sideOffset={4}>
            {tooltip}
          </TooltipContent>
        </TooltipPortal>
      )}
    </Tooltip>
  )
}
